import React from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';

/**
 * «Cambios sin guardar» — sólo al abrir OTRO informe habiendo trabajo que perder (`hasUnsavedWork`;
 * cambiar de cliente no pregunta: mockup). Tres salidas: guardar y continuar, descartar, cancelar.
 *
 * Los tres botones son de UNA familia (`-alt`), como el footer del modal de baja: `-negative` a
 * secas hereda el `.btn` base —MAYÚSCULAS, 3.2em, otra tipografía— y al lado del `-alt` de Guardar
 * salían de dos juegos distintos, con el único relleno del footer en el naranja de Guardar. Leído
 * así, «Descartar y abrir» no parecía una salida disponible sino un gemelo de «Cancelar», y el
 * modal se sentía como «guardá o guardá» (card eo7J0qds). La jerarquía la da el RELLENO: sólido
 * el que avanza por default, outline las dos salidas.
 *
 * Con `leaving`, es el aviso de SALIR de la pantalla con cambios (la solapa, el menú lateral o la
 * miga, card 7yIKxqZj): la navegación ya está frenada y sólo hay dos salidas, seguir editando
 * (el default, sólido) o irse sin guardar. Guardar no se ofrece: el modal de guardado tiene sus
 * propias preguntas y la navegación frenada no puede quedar colgada detrás de él.
 */
export function UnsavedChangesModal({isOpen, onSave, onDiscard, onCancel, informeDirty, sheetModified, hasTemplate,
                                     leaving = false}) {
    const {t} = useTranslation();
    return (
        <Modal isOpen={isOpen} toggle={onCancel} className="inf-unsaved-modal"
               data-testid={leaving ? 'leave-informe-modal' : 'unsaved-changes-modal'}>
            <ModalHeader toggle={onCancel}>
                {leaving ? t('informes.leave.title', '¿Salir sin guardar?') : t('informes.unsaved.title', 'Cambios sin guardar')}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p>{leaving
                        ? t('informes.leave.text', 'Este informe tiene cambios que todavía no guardaste. Si sales ahora, se pierden.')
                        : t('informes.unsaved.lead', 'Si abres otro informe, lo que cambiaste aquí se pierde.')}</p>
                    <ul className="inf-unsaved-list">
                        {informeDirty && <li>{t('informes.unsaved.informe', 'El informe (nombre, ajustes o modo)')}</li>}
                        {/* La hoja se nombra por lo que ES en cada caso: «la plantilla» sólo cuando
                            hay una elegida. Antes colgaba de `templateModified`, que sin plantilla
                            es siempre `false`, así que un informe nuevo con paneles agregados no los
                            mencionaba (review C-informes-06). */}
                        {sheetModified && (
                            <li>{hasTemplate
                                ? t('informes.unsaved.template', 'La plantilla (métricas, dimensiones o gráficos)')
                                : t('informes.unsaved.sheet', 'Las métricas, dimensiones y gráficos')}</li>
                        )}
                    </ul>
                </div>
            </ModalBody>
            {leaving ? (
                <ModalFooter>
                    <button type="button" className="btn -alt -negative" onClick={onDiscard} data-testid="leave-go">{t('informes.leave.go', 'Salir sin guardar')}</button>
                    <button type="button" className="btn -alt" onClick={onCancel} data-testid="leave-stay">{t('informes.leave.stay', 'Seguir editando')}</button>
                </ModalFooter>
            ) : (
                <ModalFooter>
                    <button type="button" className="btn -alt -negative" onClick={onCancel}>{t('common.cancel', 'Cancelar')}</button>
                    <button type="button" className="btn -alt -negative" onClick={onDiscard} data-testid="unsaved-discard">{t('informes.unsaved.discard', 'Descartar y abrir')}</button>
                    <button type="button" className="btn -alt" onClick={onSave} data-testid="unsaved-save">{t('informes.unsaved.save', 'Guardar y continuar')}</button>
                </ModalFooter>
            )}
        </Modal>
    );
}
